/**
 * nativeBridge.ts
 *
 * App / WebView 与 H5 之间的原生通信工具。
 * - channelCode：从 URL 参数中读取并持久化到 sessionStorage，供注册埋点上报使用。
 * - iOS：window.webkit.messageHandlers[eventName].postMessage(对象)
 * - Android：window.jsBridge.postMessage（优先双参 handler + JSON，失败再尝试仅传 JSON 字符串）
 */

const CHANNEL_CODE_KEY = 'ssg_channel_code';
const CHANNEL_CODE_DOC_KEY = 'channelCode';

// ─────────────────────────── channelCode ────────────────────────────

/**
 * 从当前 URL 的 query string 中读取 channelCode，并写入 sessionStorage。
 * 应在应用启动时调用一次；若 URL 中不存在该参数则不覆盖已存储的值。
 */
export function initChannelCode(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('channelCode')
      || params.get('channelcode')
      || Array.from(params.entries()).find(([key]) => key.toLowerCase() === 'channelcode')?.[1];
    if (code) {
      window.sessionStorage.setItem(CHANNEL_CODE_KEY, code);
      window.sessionStorage.setItem(CHANNEL_CODE_DOC_KEY, code);
    }
  } catch {
    // ignore – sessionStorage may be unavailable in some environments
  }
}

/**
 * 读取当前会话中保存的 channelCode，未设置时返回空字符串。
 */
export function getChannelCode(): string {
  try {
    return window.sessionStorage.getItem(CHANNEL_CODE_KEY)
      ?? window.sessionStorage.getItem(CHANNEL_CODE_DOC_KEY)
      ?? '';
  } catch {
    return '';
  }
}

// ──────────────────────── 原生 WebView 通信 ─────────────────────────

interface NativeEventPayload {
  eventName: string;
  params: Record<string, unknown>;
}

/**
 * 通知原生：同时尝试 iOS WKWebView 与 Android jsBridge。
 * handlerName：与 iOS 注册的 message handler 名称一致，如 eventRegister / eventMessageSent。
 */
function callNativeEvent(handlerName: string, payload: NativeEventPayload): void {
  const w = window as any;
  const json = JSON.stringify(payload);

  try {
    w.webkit?.messageHandlers?.[handlerName]?.postMessage(payload);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[nativeBridge] iOS', err);
  }

  const bridge = w.jsBridge;
  if (!bridge || typeof bridge.postMessage !== 'function') {
    return;
  }

  try {
    // 常见写法：postMessage(handlerName, jsonString)
    bridge.postMessage(handlerName, json);
  } catch {
    try {
      // 仅接收一个 JSON 字符串时，payload 内已有 eventName，原生可按 eventName 分发
      bridge.postMessage(json);
    } catch (err2) {
      // eslint-disable-next-line no-console
      console.warn('[nativeBridge] jsBridge', err2);
    }
  }
}

/**
 * 注册成功事件（iOS messageHandlers + Android jsBridge）。
 */
export function callEventRegisterNative({ channelCode, uid }: { channelCode: string; uid: string }): void {
  callNativeEvent('eventRegister', {
    eventName: 'register',
    params: {
      channelCode: String(channelCode || ''),
      uid: String(uid || ''),
      timestamp: String(Date.now()),
    },
  });
}

/**
 * 发送消息成功事件（iOS messageHandlers + Android jsBridge）。
 */
export function callEventMessageSentNative({ channelCode, uid }: { channelCode: string; uid: string }): void {
  callNativeEvent('eventMessageSent', {
    eventName: 'messageSent',
    params: {
      channelCode: String(channelCode || ''),
      uid: String(uid || ''),
      timestamp: String(Date.now()),
    },
  });
}
